import BootstrapTooltip from "@/react/bootstrap/components/BootstrapTooltip";
import UiLoader from "@/react/story/components/ui/UiLoader";
import { IriEnums } from "@/react/story/enums/IriEnums";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import type { Story, StoryPage } from "@/react/story/types";
import ApiUtils from "@/react/story/utils/ApiUtils";
import { useQuery } from "@tanstack/react-query";

type Props = {
    story: Story;
    onCopyAll: (pages: StoryPage[]) => void;
    onAddStory: () => void;
    onCopy: (page: StoryPage) => void;
    onCancel: () => void;
}

export default function PageAddStoryPages(props: Props) {
    const Story = useStory();
    const Items = useItems();

    const QueryPages = useQuery({
        queryKey: [ "add-story-pages", props.story.id ],
        queryFn: () => ApiUtils.getCollection<StoryPage>(`${IriEnums.STORY}/${props.story.id}/pages`, { deep: true }),
        refetchOnWindowFocus: false,
    });

    // L'imbrication est limitée à un niveau — c'est la profondeur que savent rendre
    // `Business\Stories` côté serveur et `getRoutes` côté client. Embarquer une story qui
    // en embarque déjà une autre produirait une branche que l'application ignore en
    // silence, alors on coupe le bouton ici en plus du refus renvoyé par l'API.
    const QueryNested = useQuery({
        queryKey: [ "add-story-nested", props.story.id ],
        queryFn: () => ApiUtils.getCollection<Story>(`${IriEnums.STORY}/${props.story.id}/stories`),
        refetchOnWindowFocus: false,
    });

    const pages = QueryPages?.data?.member ?? [];
    const nestsAStory = 0 < (QueryNested?.data?.member ?? []).length;
    const disabled = Story.story.id === props.story.id || 0 === pages.length || Items.getNavs().some(n => n.story?.id === props.story.id);
    const addStoryDisabled = disabled || nestsAStory;

    // Le grisage ne passe pas par l'attribut `disabled` : celui-ci sort le bouton du parcours
    // de tabulation et n'émet plus d'événement de survol, donc l'infobulle qui explique le
    // refus deviendrait inatteignable — or c'est le seul endroit où la règle d'imbrication
    // est expliquée. On garde la classe Bootstrap `.disabled` pour l'apparence, et on ajoute
    // les trois pièces qui lui manquent : `aria-disabled` pour l'annoncer aux technologies
    // d'assistance, `pe-auto` pour annuler le `pointer-events: none` que `.disabled` pose et
    // rendre l'infobulle à nouveau survolable, et une garde en tête du gestionnaire de clic.
    // Sans cette garde, le clavier (et la souris, une fois `pe-auto` posé) déclenchait quand
    // même l'action : l'API la refusait en 422, mais rien ne s'affichait à l'écran.

    return (
        <div className="d-flex flex-column gap-3">
            <h3 className="mb-0 text-truncate">Story : "{props.story.name}"</h3>
            <div className="d-flex gap-3">
                <button
                    type="button"
                    aria-disabled={disabled}
                    onClick={() => {
                        if (disabled) {
                            return;
                        }

                        props.onCopyAll(pages);
                    }}
                    className={`btn btn-outline-success d-flex gap-1 ${disabled ? "disabled pe-auto" : ""}`}
                >
                    <span className="bi-plus"></span>
                    <span style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>Copier toutes les pages</span>
                </button>
                <BootstrapTooltip title={nestsAStory ? "Cette story en embarque déjà une autre : l'imbrication est limitée à un niveau." : "Ajouter toute la story"}>
                    <button
                        type="button"
                        aria-disabled={addStoryDisabled}
                        onClick={() => {
                            if (addStoryDisabled) {
                                return;
                            }

                            props.onAddStory();
                        }}
                        className={`btn btn-outline-success d-flex gap-1 ${addStoryDisabled ? "disabled pe-auto" : ""}`}
                    >
                        <span className="bi-plus"></span>
                        <span style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>Ajouter toute la story</span>
                    </button>
                </BootstrapTooltip>
                <BootstrapTooltip title="Annuler">
                    <button type="button" className="btn btn-outline-secondary ms-auto" onClick={() => props.onCancel()}>
                        <span className="bi-x"></span>
                    </button>
                </BootstrapTooltip>
            </div>
            {0 < pages.length ? (
                <div className="list-group">
                    {QueryPages.isFetching ? (
                        <UiLoader />
                    ) : pages.map((p, i) => (
                        <button
                            key={p["@id"]}
                            type="button"
                            onClick={() => props.onCopy(p)}
                            className="list-group-item list-group-item-action d-flex gap-1 align-items-center border-success"
                        >
                            <div className="badge text-bg-success">
                                <span className="bi bi-plus"></span>
                            </div>
                            <span>{i + 1}/ {p.name}</span>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="alert alert-info" role="alert">
                    <p className="mb-0">Cette story n'a pas encore de page.</p>
                </div>
            )}
        </div>
    );
}
